/**
 * messagingStyles.js - Shared Style Constants for Messaging Panel
 * 
 * Translated from MessagingPanel_source.tsx (v1.1-admin-uplift)
 */

// ─── Color Constants ────────────────────────────────────────────────────────────

export const COLORS = {
  // Primary palette
  gold: '#C6A75A',
  goldLight: 'rgba(198,167,90,0.15)',
  goldMedium: 'rgba(198,167,90,0.22)',
  goldDark: '#8A7241',
  goldText: '#876B2C',
  
  // Surfaces
  surface: '#FFFEFB',
  surfaceWarm: '#FFFDF8',
  surfaceLight: '#FFFCF7',
  inputBg: '#FFFCF7',
  chipBg: '#FFFFFF',
  chipActive: '#FFF4D7',
  previewFrame: '#E4DBC9',
  
  // Text
  textPrimary: '#1F1C17',
  textSecondary: '#463F34',
  textMuted: '#8B8478',
  textPlaceholder: '#9D9587',
  buttonText: '#463F34',
  
  // Status colors
  success: '#4A8C5A',
  successLight: 'rgba(74,140,90,0.15)',
  error: '#dc2626',
  errorLight: 'rgba(220,38,38,0.15)',
  warning: '#f59e0b',
  warningLight: 'rgba(245,158,11,0.15)',
  info: '#3b82f6',
  infoLight: 'rgba(59,130,246,0.15)',
  
  // Borders
  border: 'rgba(198,167,90,0.18)',
  borderLight: 'rgba(198,167,90,0.12)',
  borderMedium: 'rgba(198,167,90,0.22)',
};

// ─── Shared Styles ──────────────────────────────────────────────────────────────

export const styles = {
  // ═══ TAB RAIL (A1-A6) ═════════════════════════════════════════════════════════
  tabRailShell: {
    borderBottom: `1px solid ${COLORS.border}`,
    background: COLORS.surfaceWarm,
    padding: '0 24px',
  },
  tabRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingTop: 16,
    paddingBottom: 16,
  },
  tabChip: {
    padding: '10px 20px',
    borderRadius: 999,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.chipBg,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  tabChipActive: {
    background: COLORS.gold,
    borderColor: COLORS.gold,
  },
  tabChipText: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textSecondary,
    letterSpacing: '-0.1px',
  },
  tabChipTextActive: {
    color: '#FFFFFF',
  },

  // ═══ COMMAND BAR (B1-B5) ══════════════════════════════════════════════════════
  commandBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: `1px solid ${COLORS.borderLight}`,
    background: COLORS.surface,
  },
  commandBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  commandBarTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  commandBarIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: COLORS.goldLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  commandBarHeading: {
    fontSize: 18,
    fontWeight: 700,
    color: COLORS.textPrimary,
    letterSpacing: '-0.3px',
  },
  commandBarSubheading: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginTop: 2,
  },
  commandBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  commandBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    borderRadius: 12,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.goldDark,
  },
  commandBtnPrimary: {
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    border: 'none',
    color: '#FFFFFF',
    boxShadow: '0 4px 12px rgba(198,167,90,0.25)',
  },
  filterChipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  filterChip: {
    padding: '6px 12px',
    borderRadius: 8,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.chipBg,
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 600,
    color: COLORS.textMuted,
    transition: 'all 0.15s ease',
  },
  filterChipActive: {
    background: COLORS.chipActive,
    borderColor: COLORS.gold,
    color: COLORS.goldDark,
  },
  searchInput: {
    padding: '10px 14px 10px 36px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.inputBg,
    fontSize: 13,
    color: COLORS.textPrimary,
    width: 200,
    outline: 'none',
    transition: 'border-color 0.15s ease',
  },
  searchInputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    pointerEvents: 'none',
  },

  // ═══ PRE-FLIGHT HUD (C1-C9) ═══════════════════════════════════════════════════
  preflightHUD: {
    position: 'relative',
    padding: 20,
    borderRadius: 18,
    border: `1px solid ${COLORS.borderMedium}`,
    background: `linear-gradient(180deg, rgba(198,167,90,0.12) 0%, rgba(198,167,90,0.06) 50%, rgba(255,255,255,0.00) 100%)`,
    marginBottom: 20,
    overflow: 'hidden',
  },
  preflightNeonTopBar: {
    position: 'absolute',
    top: 0,
    left: 20,
    right: 20,
    height: 2,
    background: `linear-gradient(90deg, transparent, ${COLORS.gold}, transparent)`,
    borderRadius: 1,
  },
  preflightEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: COLORS.goldDark,
    marginBottom: 4,
  },
  preflightTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 16,
    letterSpacing: '-0.2px',
  },
  preflightRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  preflightIndicatorWrap: {
    position: 'relative',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  preflightCheckCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    background: COLORS.success,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 2px 8px ${COLORS.successLight}`,
  },
  preflightPendingWrap: {
    position: 'relative',
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  preflightHaloRing: {
    position: 'absolute',
    width: 20,
    height: 20,
    borderRadius: 10,
    border: `2px solid ${COLORS.gold}`,
  },
  preflightPendingDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    background: COLORS.gold,
  },
  preflightItemLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: COLORS.textSecondary,
  },
  preflightItemLabelDone: {
    color: COLORS.success,
  },
  preflightItemStatus: {
    fontSize: 10,
    color: COLORS.textMuted,
    marginTop: 2,
  },
  preflightItemStatusDone: {
    color: COLORS.success,
  },

  // ═══ TEMPLATE CAROUSEL (D1-D14) ═══════════════════════════════════════════════
  templateCarouselShell: {
    position: 'relative',
    padding: 20,
    borderRadius: 18,
    border: `1px solid ${COLORS.borderMedium}`,
    background: `linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,252,246,0.72) 50%, rgba(255,248,234,0.40) 100%)`,
    overflow: 'hidden',
  },
  carouselHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  carouselEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: COLORS.goldDark,
    marginBottom: 4,
  },
  carouselCountLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textSecondary,
  },
  carouselNavRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  carouselNavBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  carouselNavBtnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  carouselPageLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: COLORS.textMuted,
    minWidth: 40,
    textAlign: 'center',
  },
  
  // Glass Card (Template Card)
  glassCard: {
    position: 'relative',
    padding: 18,
    borderRadius: 16,
    border: `1px solid rgba(255,255,255,0.5)`,
    background: `linear-gradient(180deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.44) 50%, rgba(255,255,255,0.10) 100%)`,
    backdropFilter: 'blur(12px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    overflow: 'hidden',
  },
  glassCardArmed: {
    border: `2px solid ${COLORS.gold}`,
    background: `linear-gradient(180deg, rgba(198,167,90,0.22) 0%, rgba(198,167,90,0.12) 50%, rgba(255,255,255,0.08) 100%)`,
    boxShadow: `0 8px 32px rgba(198,167,90,0.2), inset 0 1px 0 rgba(255,255,255,0.9)`,
  },
  glassArmedNeonBorder: {
    position: 'absolute',
    top: -1,
    left: -1,
    right: -1,
    bottom: -1,
    borderRadius: 17,
    border: `2px solid ${COLORS.gold}`,
    boxShadow: `0 0 12px ${COLORS.gold}`,
    pointerEvents: 'none',
  },
  glassInsetHighlight: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 1,
    background: 'rgba(255,255,255,0.9)',
    borderRadius: '16px 16px 0 0',
  },
  glassCardContent: {
    position: 'relative',
    zIndex: 1,
  },
  glassTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  glassStatusPill: {
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  glassStatusReady: {
    background: COLORS.successLight,
  },
  glassStatusDraft: {
    background: 'rgba(107,114,128,0.15)',
  },
  glassStatusText: {
    fontSize: 10,
    fontWeight: 700,
  },
  glassStatusTextReady: {
    color: COLORS.success,
  },
  glassStatusTextDraft: {
    color: '#6B7280',
  },
  glassArmedBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    borderRadius: 6,
    background: COLORS.goldLight,
  },
  glassArmedText: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.gold,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  glassCardTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 6,
    letterSpacing: '-0.2px',
    lineHeight: 1.3,
  },
  glassCardSubject: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginBottom: 14,
    lineHeight: 1.4,
  },
  glassCardFooterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  glassCardType: {
    fontSize: 11,
    fontWeight: 600,
    color: COLORS.goldDark,
    textTransform: 'capitalize',
  },
  glassCardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  glassActionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 10px',
    borderRadius: 8,
    border: `1px solid ${COLORS.borderMedium}`,
    background: 'rgba(255,255,255,0.6)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  glassActionText: {
    fontSize: 11,
    fontWeight: 600,
    color: COLORS.goldDark,
  },
  
  // Empty State
  carouselEmptyCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    borderRadius: 16,
    border: `1px dashed ${COLORS.borderMedium}`,
    background: 'rgba(255,255,255,0.5)',
    textAlign: 'center',
  },
  carouselEmptyTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginTop: 12,
    marginBottom: 6,
  },
  carouselEmptyBody: {
    fontSize: 12,
    color: COLORS.textMuted,
    lineHeight: 1.5,
    maxWidth: 200,
  },

  // ═══ EMAIL STUDIO LAYOUT ══════════════════════════════════════════════════════
  emailStudio3Col: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr 320px',
    gap: 24,
    padding: 24,
    minHeight: 'calc(100vh - 200px)',
  },
  emailStudio3ColStacked: {
    gridTemplateColumns: '1fr',
  },
  studioLeftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  studioCenterColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  studioRightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
};

// ─── Keyframes Injection ────────────────────────────────────────────────────────

if (typeof document !== 'undefined') {
  const styleId = 'messaging-panel-keyframes';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes preflightHaloPulse {
        0%, 100% { opacity: 0.22; transform: scale(0.82); }
        50% { opacity: 0.72; transform: scale(1.26); }
      }
      @keyframes messagingFadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes messagingSlideUp {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes carouselScale {
        0% { transform: scale(1); }
        50% { transform: scale(0.92); }
        100% { transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
  }
}

export default { COLORS, styles };
